import { Dispatch, MiddlewareAPI } from "redux";
import Config from "../../../../config/config";
import ApiUrls from "../../../../constants/ApiUrls";
import CheckoutSteps from "../../../../constants/CheckoutSteps";
import CosmosModels from "../../../../constants/CosmosModels";
import MiddlewareEntities from "../../../../constants/MiddlewareEntities";
import NotificationTypes from "../../../../constants/NotificationTypes";
import { OrderConstants } from "../../../../constants/OrderConstants";
import PageUrls from "../../../../constants/PageUrls";
import createid from "../../../../utilites/createid";
import { getOrderSubmitParams, stateHasOrderSubmitParams } from "../../../../utilites/orderSubmitUtilities";
import { isAdditionalProductsPage, prepareUrlForRedirectToCheckout } from "../../../../utilites/util";
import { pushEmptyCartToAnalyticsAction } from "../../analytics/actions";
import { apiRequest } from "../../api/actions";
import { removeUserLabelMessage, setUserLabelMessage } from "../../labelUserMessage/actions";
import { getNotification, setNotification } from "../../notification/actions";
import { INotification } from "../../notification/types";
import { clearPreBasketAction } from "../../preBasket/actions";
import { redirectToAction } from "../../redirection/actions";
import { AppState } from "../../rootReducers";
import { getSessionData } from "../../sessionData/actions";
import { ISessionData } from "../../sessionData/types";
import { setLoader } from "../../ui/actions";
import { cancelOrderAction } from "../manhattan/actions";
import { getOrderFromCookieAction } from "../manhattan/transition/actions";
import { ISubmitAccessoriesOrderRequestProduct } from "./types";
import { clearAdditionalProducts } from "../../../../utilites/additionalProductsUtils";
import { MAIN_PRODUCT_STATE_SNAPSHOT } from "../../../../constants/SessionStorageKeys";

interface OrderSubmitApiSuccessPayload {
  data : {
    session : ISessionData
  }
}

interface OrderSubmitApiErrorPayload {
  data : {
    code: string,
    status: number,
    message: string,
  }
}

abstract class ISubmitOrderMiddlewareProxy {

  public static orderSubmit: (state: AppState, next: Dispatch) => void;
  public static orderSubmitApiSuccess: (state: AppState, next: Dispatch, payload: OrderSubmitApiSuccessPayload) => void;
  public static orderSubmitApiError: (state: AppState, next: Dispatch) => void;
  public static orderClearBasket: (state: AppState, store: MiddlewareAPI) => void;
  public static orderSubmitAccessory: (state: AppState, next: Dispatch) => void;
  public static orderSubmitAccessoryApiSuccess: (next: Dispatch, payload: OrderSubmitApiSuccessPayload) => void;

}

export default class SubmitOrderMiddlewareProxy extends ISubmitOrderMiddlewareProxy {

  public static orderSubmit(state: AppState, next: Dispatch): void {
    const language = state.language.language;
    const section = state.section?.section;

    // Check if we need to offer additional products first.
    if (!stateHasOrderSubmitParams(state)) {
      sessionStorage.setItem(MAIN_PRODUCT_STATE_SNAPSHOT, JSON.stringify(state));

      const additionalProductsPage = prepareUrlForRedirectToCheckout(language, section, PageUrls[language].ADDITIONAL_PRODUCTS);
      next(redirectToAction(additionalProductsPage));
      return;
    }

    next(apiRequest(
      null,
      'POST',
      `${ApiUrls.ORDER_SUBMIT}`,
      null,
      getOrderSubmitParams(state),
      MiddlewareEntities.ORDER.ORDER_SUBMIT,
      {}),
    );
    next(setLoader(true, MiddlewareEntities.ORDER.ORDER_SUBMIT));
  }

  public static orderSubmitApiSuccess(state: AppState, next: Dispatch, payload: OrderSubmitApiSuccessPayload): void {
    const language = state.language.language as "sr" | "en";
    const section = state.section?.section;
    const nextPage = prepareUrlForRedirectToCheckout(language, section, CheckoutSteps[language].checkout);

    clearAdditionalProducts();

    if (isAdditionalProductsPage()) {
      next(redirectToAction(nextPage));
    }

    next(getSessionData(payload.data.session));
    next(getNotification(payload.data.session.flashMessages, MiddlewareEntities.ORDER.ORDER_SUBMIT));
    next(getOrderFromCookieAction());

    next(redirectToAction(nextPage));
  }

  public static orderSubmitApiError(state: AppState, next: Dispatch): void {
    const language = state.language.language;
    const labels = require(`../../../../translations/${language}`);

    const errorNotification: INotification[] = [{
      id: parseInt(createid("")),
      type: NotificationTypes.types.ERROR,
      message: labels.errorMessage,
      cssClass: NotificationTypes.cssClass.ALERT_DANGER
    }];


    next(setNotification(errorNotification, MiddlewareEntities.ORDER.ORDER_SUBMIT));
    next(setLoader(false, MiddlewareEntities.ORDER.ORDER_SUBMIT));
  }

  public static orderClearBasket(state: AppState, store: MiddlewareAPI): void {
    const language = state.language.language;
    const { ore } = state.manhattanOrder.order.basket;

    store.dispatch(pushEmptyCartToAnalyticsAction());
    store.dispatch(cancelOrderAction());
    store.dispatch(clearPreBasketAction());
    store.dispatch(removeUserLabelMessage());

    if (ore) {
      const { offerId } = ore;
      const offerUrl = offerId ? `?offerId=${offerId}` : '';
      store.dispatch(redirectToAction(`${Config.prefixUrl}/${language}/campaigns/list${offerUrl}`));
    }
  }

  public static orderSubmitAccessory = (state: AppState, next: Dispatch) => {
    const section = state.section?.section;
    const accessoriesProductData = state.productData;
    const accessoriesOrderItemId = state.searchParams.productOverview?.orderItemId;

    const submitAccessoriesOrderParams: ISubmitAccessoriesOrderRequestProduct = {
      segment: section,
      selectedProducts: [{
        id: Number(accessoriesProductData.product.item.id),
        productLine: accessoriesProductData.product.item.productLine.productLine,
        purchaseType: accessoriesProductData.productData.purchase,
        colorId: parseInt(accessoriesProductData.productData.color),
        cosmos: null,
        installments: 0,
        contractDuration: 0,
        ...accessoriesOrderItemId && { orderItemId: Number(accessoriesOrderItemId) },
      }],
    };

    if (accessoriesOrderItemId) {
      submitAccessoriesOrderParams.selectedProducts[0].orderItemId = Number(accessoriesOrderItemId);
    }

    // Add additional submitOrder params for COSMOS if it is set
    if (accessoriesProductData.productData.purchase === OrderConstants.purchases.COSMOS || accessoriesProductData.productData.cosmosInstallments === 1) {
      submitAccessoriesOrderParams.selectedProducts[0].installments = accessoriesProductData.productData.cosmosInstallments;
      submitAccessoriesOrderParams.selectedProducts[0].contractDuration = accessoriesProductData.productData.cosmosInstallments;
      submitAccessoriesOrderParams.selectedProducts[0].cosmos = {
        cosmosModel: CosmosModels.TELCO,
        participation: accessoriesProductData.productData.participation,
      };
      if (accessoriesProductData.productData.cosmosInstallments === 1 && accessoriesProductData.productData.purchase === OrderConstants.purchases.SUBSIDY) submitAccessoriesOrderParams.selectedProducts[0].purchaseType = 'cosmos';
    }

    next(apiRequest(
      null,
      'POST', `${ApiUrls.ORDER_SUBMIT}`,
      null,
      submitAccessoriesOrderParams,
      MiddlewareEntities.ORDER.ACCESSORIES_ORDER_SUBMIT,
      {}),
    );
    next(setLoader(true, MiddlewareEntities.ORDER.ACCESSORIES_ORDER_SUBMIT));
  };

  public static orderSubmitAccessoryApiSuccess = (next: Dispatch, payload: OrderSubmitApiSuccessPayload) => {
    next(getSessionData(payload.data.session));
    next(getNotification(payload.data.session.flashMessages, MiddlewareEntities.ORDER.ACCESSORIES_ORDER_SUBMIT));
    next(setLoader(false, MiddlewareEntities.ORDER.ACCESSORIES_ORDER_SUBMIT));
  };

  public static orderSubmitAccessoryApiError(state: AppState, next: Dispatch, payload: OrderSubmitApiErrorPayload): void {
    const language = state.language.language;
    const labels = require(`../../../../translations/${language}`);

    if (payload.data.status === 403) {
      next(setUserLabelMessage("wantToBuyProductsByChangingPurchase", MiddlewareEntities.ORDER.ACCESSORIES_ORDER_SUBMIT));
      next(setLoader(false, MiddlewareEntities.ORDER.ACCESSORIES_ORDER_SUBMIT));
    } else {
      const errorNotification: INotification[] = [{
        id: parseInt(createid("")),
        type: NotificationTypes.types.ERROR,
        message: labels.errorMessage,
        cssClass: NotificationTypes.cssClass.ALERT_DANGER
      }];

      next(setNotification(errorNotification, MiddlewareEntities.ORDER.ACCESSORIES_ORDER_SUBMIT));
      next(setLoader(false, MiddlewareEntities.ORDER.ACCESSORIES_ORDER_SUBMIT));
    }
  }

}
